/**
 * Core-value retention events — the 5 actions that define "did this user
 * actually realize the product's core value after sign-up?".
 *
 * Why a separate event name (`core_value_action`)?
 *
 * The existing `EditorTrack.*` taxonomy is a high-volume *interaction* stream
 * — it answers "what did the user do in the editor". Cohort/retention
 * dashboards care about a much smaller, distinct question: "did this user
 * cross the activation funnel at all this week / month / lifetime?". Mixing
 * the two in one event blows up the funnel-builder UI and forces every chart
 * to filter by ~30 properties.
 *
 * We solve it by emitting a thin core-value stream:
 *
 *   - `core_value_action_project_created`
 *   - `core_value_action_project_manual_edit`
 *   - `core_value_action_project_chat_edit`
 *   - `core_value_action_project_export`
 *   - `core_value_action_project_share`
 *
 * `project_created` is emitted at most once per project. Other actions can be
 * emitted repeatedly and carry project-local first/count properties.
 *
 * Each event auto-carries:
 *   - `project_id` — from `editor-context` or arg override
 *   - `is_first_for_project` — true only for the first event of this action in
 *     this project
 *   - `project_core_value_action_count` — cumulative count for this action in
 *     this project, including the current event
 *   - plus action-specific fields (creation_source, creation_mode, …)
 *
 * First/count state is internal (localStorage per project/action). User-level milestones
 * (`first_*_at`, `total_*`) go to Amplitude user properties via $setOnce / $add.
 *
 * Side effects (high-leverage, do automatically):
 *   - `$setOnce { first_<action>_at }` — earliest time we ever saw this action
 *     for this user
 *   - `$add { total_<action>: 1 }` — monotonically increasing counter
 *   - `$setOnce activated_at` — fired the first time the user crosses
 *     `manual_edit` OR `chat_edit` (definition of "activated" in our funnel).
 */

import { addUserProperties, setUserPropertiesOnce, trackEvent } from './client';
import { getBrowserContextProperties } from './context';
import { getEditorContextProperties } from './editor-context';
import type { ProjectCreatedEventProps } from './project-created-props';

export type CoreValueAction =
  | 'project_created'
  | 'project_manual_edit'
  | 'project_chat_edit'
  | 'project_export'
  | 'project_share';

/** URL pathname where the project was created, e.g. `/workspace/create` or `/`. */
export type ProjectCreationSource = string;

export type ProjectCreationMode = 'ai_generate' | 'manual' | 'template';

interface BaseExtra {
  project_id: string;
}

interface ProjectCreatedExtra extends Omit<ProjectCreatedEventProps, 'project_id'> {
  project_id: string;
}

interface ProjectExportExtra extends BaseExtra {
  format?: string;
  destination?: 'download' | 'system_share';
  method?: string;
  count?: number;
  resolution?: number;
  base_width?: number;
  base_height?: number;
  output_width?: number;
  output_height?: number;
  file_name?: string;
  output_extension?: string;
  print_optimized?: boolean;
  cmyk_mode?: boolean;
  dpi?: number;
  bleed_size?: number;
  print_width?: number;
  print_height?: number;
  print_size_locked?: boolean;
  badge_style?: string;
  video_fps?: number;
  video_duration?: number;
  project_export_count?: number;
  is_project_first_export?: boolean;
  project_first_exported_at?: string;
  frame_ids?: string;
  frame_count?: number;
  frame_export_counts?: string;
  frame_first_exported_ats?: string;
  frame_first_export_flags?: string;
  frame_export_count?: number;
  is_frame_first_export?: boolean;
  frame_first_exported_at?: string;
}

interface ProjectShareExtra extends BaseExtra {
  visibility?: string;
  share_method?: 'copy_link' | 'publish_template' | 'social' | 'embed';
}

interface ProjectEditExtra extends BaseExtra {
  /** Property / canvas action that triggered the first edit. */
  trigger?: string;
}

type CoreValueExtra =
  | ProjectCreatedExtra
  | ProjectEditExtra
  | ProjectExportExtra
  | ProjectShareExtra;

// =============================================================================
// localStorage state keyed by `{action}:p:{projectId}`.
// Older versions stored `"1"` for first-only dedupe; we read that as count=1.
// =============================================================================

const STORAGE_PREFIX = 'core_value_action_v1';

interface ProjectActionState {
  count: number;
  first_at?: string;
}

function safeLocalStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function readProjectActionState(key: string): ProjectActionState {
  const ls = safeLocalStorage();
  if (!ls) return { count: 0 };
  try {
    const raw = ls.getItem(`${STORAGE_PREFIX}:${key}`);
    if (!raw) return { count: 0 };
    if (raw === '1') return { count: 1 };
    const parsed = JSON.parse(raw) as Partial<ProjectActionState>;
    return {
      count: typeof parsed.count === 'number' ? parsed.count : 0,
      first_at: typeof parsed.first_at === 'string' ? parsed.first_at : undefined,
    };
  } catch {
    return { count: 0 };
  }
}

function writeProjectActionState(key: string, state: ProjectActionState) {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    ls.setItem(`${STORAGE_PREFIX}:${key}`, JSON.stringify(state));
  } catch {
    // ignore quota / private mode
  }
}

// =============================================================================
// Public API — `CoreValueTrack.<action>({ project_id, ... })`
// =============================================================================

function fireCoreValueEvent(action: CoreValueAction, extra: CoreValueExtra): void {
  const ctx = getEditorContextProperties();
  const projectId = extra.project_id || (ctx.project_id as string) || '';
  if (!projectId) return;

  const projectKey = `${action}:p:${projectId}`;
  const previous = readProjectActionState(projectKey);
  if (action === 'project_created' && previous.count > 0) return;

  const now = new Date().toISOString();
  const nextState: ProjectActionState = {
    count: previous.count + 1,
    first_at: previous.first_at ?? now,
  };
  writeProjectActionState(projectKey, nextState);
  const isFirstForProject = previous.count === 0;

  trackEvent(`core_value_action_${action}`, {
    ...ctx,
    ...flattenPrimitives(extra as unknown as Record<string, unknown>),
    is_first_for_project: isFirstForProject,
    project_core_value_action_count: nextState.count,
    project_core_value_action_first_at: nextState.first_at,
  });

  // User milestones — $setOnce / $add are idempotent server-side.
  setUserPropertiesOnce({
    [`first_${action}_at`]: now,
  });
  if (action === 'project_manual_edit' || action === 'project_chat_edit') {
    setUserPropertiesOnce({
      activated_at: now,
      activation_trigger: action,
    });
  }
  addUserProperties({ [`total_${action}`]: 1 });
}

function flattenPrimitives(obj: Record<string, unknown>) {
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) {
      if (v.length === 0) continue;
      out[k] = v.map(String).join('|');
      continue;
    }
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      out[k] = v;
    }
  }
  return out;
}

export const CoreValueTrack = {
  projectCreated: (extra: ProjectCreatedExtra) => {
    const pagePath = getBrowserContextProperties().page_path;
    return fireCoreValueEvent('project_created', {
      ...extra,
      creation_source:
        extra.creation_source ?? (typeof pagePath === 'string' && pagePath ? pagePath : 'unknown'),
    });
  },
  projectManualEdit: (extra: ProjectEditExtra) => fireCoreValueEvent('project_manual_edit', extra),
  projectChatEdit: (extra: ProjectEditExtra) => fireCoreValueEvent('project_chat_edit', extra),
  projectExport: (extra: ProjectExportExtra) => fireCoreValueEvent('project_export', extra),
  projectShare: (extra: ProjectShareExtra) => fireCoreValueEvent('project_share', extra),
};
